import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/integrated-auth';
import { ChatMessageList } from '../chat/chat-message-list';
import { ChatInput } from '../chat/chat-input';
import { ChatReportModal } from '../chat/chat-report-modal';
import { PersonalMutesPanel } from '../chat/personal-mutes-panel';
import { useDailyChat, type ChatMessage, type GifData, type TrackData } from '../chat/hooks/use-daily-chat';
import { useBotTyping } from '../chat/hooks/use-bot-typing';
import { useChatStatus } from '../chat/hooks/use-chat-status';
import { usePersonalMutes } from '../chat/hooks/use-personal-mutes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Shield, Loader2, Users, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface DailyRoomChatProps {
  roomId: number;
  roomName: string;
  userId: string | null;
  username: string | null;
  avatarUrl: string | null;
  fullScreen?: boolean;
}

export function DailyRoomChat({
  roomId,
  roomName,
  userId,
  username,
  avatarUrl,
  fullScreen = false,
}: DailyRoomChatProps) {
  const { user, firebaseUser } = useAuth();
  const { toast } = useToast();
  const [reportingMessage, setReportingMessage] = useState<ChatMessage | null>(null);

  // Check if user is banned from chat platform-wide
  const { data: chatStatus, isLoading: statusLoading } = useChatStatus();

  // Get current user info - use props primarily, fallback to context
  // IMPORTANT: Use Firebase Auth UID for chat since Firestore messages store the auth UID
  const currentUserId = userId || firebaseUser?.uid || null;
  const chatUserId = firebaseUser?.uid || null; // Always Firebase UID for Firestore matching
  const isFullyAuthenticated = !!(currentUserId && (username || user?.username));
  const currentUsername = username || user?.username || firebaseUser?.displayName || null;
  const currentAvatarUrl = avatarUrl || user?.avatarImageUrl || firebaseUser?.photoURL || null;

  // Bot typing indicators
  const typingBots = useBotTyping(roomId);

  // Chat hook
  const chatHookEnabled = !chatStatus?.isBanned && isFullyAuthenticated;
  const {
    messages,
    isLoading: chatLoading,
    error: chatError,
    sendMessage,
    deleteMessage,
    isSending,
  } = useDailyChat({
    roomId,
    userId: currentUserId,
    username: currentUsername,
    avatarUrl: currentAvatarUrl,
    enabled: chatHookEnabled,
  });

  // Personal mutes hook (for all users - global mute list)
  const {
    mutedUsers: personalMutes,
    mutedUserIds: personalMutedUserIds,
    mutesLoading: personalMutesLoading,
    muteUser: personalMuteUser,
    unmuteUser: personalUnmuteUser,
    isUnmuting: isPersonalUnmuting,
  } = usePersonalMutes();

  // Filter messages to hide personally muted users
  const filteredMessages = useMemo(() => {
    if (personalMutedUserIds.length === 0) return messages;
    return messages.filter(msg => !personalMutedUserIds.includes(msg.userId));
  }, [messages, personalMutedUserIds]);

  // Handler for personal muting (any member can do this)
  const handlePersonalMuteUser = useCallback(
    async (targetUserId: string, targetUsername: string) => {
      try {
        await personalMuteUser(targetUserId);
        toast({
          title: 'User muted',
          description: `${targetUsername}'s messages will be hidden from you.`,
        });
      } catch (error) {
        toast({
          title: 'Failed to mute user',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [personalMuteUser, toast]
  );

  // Handler for sending text messages
  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!isFullyAuthenticated || !currentUsername) {
        throw new Error('Please wait for your profile to load');
      }
      try {
        await sendMessage({ content });
      } catch (error: any) {
        toast({
          title: 'Unable to send message',
          description: error?.message || 'Please try refreshing the page.',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [sendMessage, isFullyAuthenticated, currentUsername, toast]
  );

  // Handler for sending GIFs
  const handleSendGif = useCallback(
    async (gifData: GifData, comment?: string) => {
      if (!isFullyAuthenticated || !currentUsername) {
        toast({
          title: 'Cannot send GIF',
          description: 'Please wait for your profile to load',
          variant: 'destructive',
        });
        return;
      }

      try {
        await sendMessage({
          content: comment || '',
          contentType: 'gif',
          gifData,
        });
      } catch (error: any) {
        console.error('GIF send error:', error);
        toast({
          title: 'Failed to send GIF',
          description: error?.message || 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [sendMessage, isFullyAuthenticated, currentUsername, toast]
  );

  // Handler for sending tracks
  const handleSendTrack = useCallback(
    async (trackData: TrackData, comment?: string) => {
      if (!isFullyAuthenticated || !currentUsername) {
        toast({
          title: 'Cannot share track',
          description: 'Please wait for your profile to load',
          variant: 'destructive',
        });
        return;
      }

      try {
        await sendMessage({
          content: comment || '',
          contentType: 'track',
          trackData,
        });
      } catch (error: any) {
        console.error('Track send error:', error);
        toast({
          title: 'Failed to share track',
          description: error?.message || 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [sendMessage, isFullyAuthenticated, currentUsername, toast]
  );

  // Handler for reporting messages (uses global report endpoint)
  const handleReport = useCallback(
    async (category: string, description?: string) => {
      if (!reportingMessage) return;

      let contentForReport = reportingMessage.content;
      if (!contentForReport || contentForReport.trim() === '') {
        if (reportingMessage.contentType === 'image' && reportingMessage.imageUrl) {
          contentForReport = `[Image: ${reportingMessage.imageUrl}]`;
        } else if (reportingMessage.contentType === 'gif' && reportingMessage.gifData) {
          contentForReport = `[GIF: ${reportingMessage.gifData.url}]`;
        } else if (reportingMessage.contentType === 'track' && reportingMessage.trackData) {
          contentForReport = `[Track: ${reportingMessage.trackData.title} by ${reportingMessage.trackData.artistName}]`;
        } else {
          contentForReport = `[${reportingMessage.contentType} message]`;
        }
      }

      try {
        // Use a generic report endpoint or daily-specific one
        await apiRequest('/api/chat/report', 'POST', {
          messageId: reportingMessage.id,
          messageContent: contentForReport,
          messageAuthorId: reportingMessage.userId,
          category,
          description,
          context: 'daily_room',
          roomId,
        });
        toast({
          title: 'Report submitted',
          description: 'Thank you for helping keep our community safe.',
        });
        setReportingMessage(null);
      } catch (error) {
        toast({
          title: 'Failed to submit report',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [reportingMessage, roomId, toast]
  );

  // Loading state
  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Platform-wide ban
  if (chatStatus?.isBanned) {
    return (
      <Alert variant="destructive" className="m-4">
        <Shield className="h-4 w-4" />
        <AlertTitle>Chat Access Restricted</AlertTitle>
        <AlertDescription>
          Your chat access has been suspended.
          {chatStatus.banReason && <p className="mt-1">Reason: {chatStatus.banReason}</p>}
          {chatStatus.banExpiresAt && (
            <p className="mt-1">
              This restriction will be lifted on{' '}
              {new Date(chatStatus.banExpiresAt).toLocaleDateString()}.
            </p>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Not authenticated
  if (!isFullyAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground p-4">
        <MessageCircle className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm text-center">Sign in to chat with your group</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col bg-background ${fullScreen ? 'h-full' : 'h-[400px] rounded-lg'}`}>
      {/* Header - hidden in fullScreen mode (parent provides header) */}
      {!fullScreen && (
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-purple-500" />
            {messages.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {messages.length} message{messages.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <PersonalMutesPanel />
        </div>
      )}

      {/* Messages */}
      <ChatMessageList
        messages={filteredMessages}
        isLoading={chatLoading}
        error={chatError}
        currentUserId={chatUserId}
        isOwner={false}
        onReport={(message) => setReportingMessage(message)}
        onDelete={deleteMessage}
        onPersonalMuteUser={handlePersonalMuteUser}
        personalMutedUserIds={personalMutedUserIds}
      />

      {/* Bot typing indicators */}
      {typingBots.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground border-t">
          <div className="flex -space-x-1">
            {typingBots.slice(0, 3).map(bot => (
              bot.avatarUrl
                ? <img key={bot.userId} src={bot.avatarUrl} alt={bot.username} className="w-4 h-4 rounded-full ring-1 ring-background object-cover" />
                : <div key={bot.userId} className="w-4 h-4 rounded-full bg-purple-400 ring-1 ring-background flex items-center justify-center text-[8px] text-white font-bold">{bot.username[0]?.toUpperCase()}</div>
            ))}
          </div>
          <span>{typingBots.map(b => b.username).join(', ')} {typingBots.length === 1 ? 'is' : 'are'} typing</span>
          <span className="flex gap-0.5 items-center">
            <span className="w-1 h-1 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1 h-1 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1 h-1 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
          </span>
        </div>
      )}

      {/* Input */}
      <ChatInput
        onSendMessage={handleSendMessage}
        onSendGif={handleSendGif}
        onSendTrack={handleSendTrack}
        isSending={isSending}
        disabled={!isFullyAuthenticated}
        disabledReason={
          !currentUserId
            ? 'Sign in to chat'
            : !isFullyAuthenticated
            ? 'Loading profile...'
            : undefined
        }
        imagesAllowed={false}
      />

      {/* Report Modal */}
      <ChatReportModal
        open={!!reportingMessage}
        onOpenChange={(open) => !open && setReportingMessage(null)}
        message={reportingMessage}
        onReport={handleReport}
        isReporting={false}
      />
    </div>
  );
}
